<template>
    <RedesignFormItem
        v-if="model"
        class="widget-color-picker !mb-0"
    >
        <div class="">
            <div class="main-colors">
                <div class="font-semibold text-default-text">
                    {{ $t('widget.colors') }}
                </div>
                <div class="flex flex-col gap-1">
                    <div
                        v-for="(option, index) of mainColors"
                        :key="`${option.key}-${index}`"
                    >
                        <div>
                            <RedesignWidgetColor
                                :label="String($t(`widget.settings.color.${option.key}`))"
                                :value="option.value"
                                :predefined-colors="predefinedColors"
                                @input="($evt) => onChangeColor($evt, option.key)"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div
                v-if="getWidgetColors.length"
                class="other-colors flex flex-col gap-1"
            >
                <div class="font-semibold text-default-text">
                    {{ $t('widget.valueColors') }}
                </div>
                <div class="flex flex-col gap-1">
                    <RedesignWidgetColor
                        v-for="(option, index) of getWidgetColors"
                        :key="`${option.key}-${index}`"
                        :value="option.value"
                        :label="String($t(`widget.settings.color.${option.key}`))"
                        :predefined-colors="predefinedColors"
                        @input="($evt) => onChangeColor($evt, option.key)"
                    >
                        <template #default>
                            <slot />
                        </template>
                    </RedesignWidgetColor>
                </div>
            </div>
        </div>
    </RedesignFormItem>
</template>

<script setup lang="ts">
import RedesignFormItem from '@/components/RedesignBaseComponents/Form/RedesignFormItem.vue'
import { Colors } from '@/types/dashboard'
import { computed, onMounted } from 'vue'
import values from 'lodash/values'
import uniq from 'lodash/uniq'
import { useVModel } from '@vueuse/core'
import RedesignWidgetColor from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetColor.vue'
import { activeLayoutColors } from '@/composables/globalData/useLayouts'
import { getAllDefaultCardColors } from '@/enum/defaultWidgetSettings'

const BACKGROUND_COLORS: Colors = { background: '' }

const props = withDefaults(
    defineProps<{
        value?: Colors
        onlyBackground?: boolean
        availableColors?: Colors
    }>(),
    {
        value: undefined,
        availableColors: () => ({ ...getAllDefaultCardColors() })
    }
)

const emit = defineEmits([ 'update:value', 'input' ])

const model = useVModel(props, 'value', emit, {
    eventName: 'input',
    passive: true
})

const mainColorKeys = computed(() => {
    return props.onlyBackground ? Object.keys(BACKGROUND_COLORS) : Object.keys(getAllDefaultCardColors())
})

const mainColors = computed(() => {
    const colors = mainColorKeys.value as Array<keyof Colors>
    return colors.map(colorKey => {
        const modelValue: Colors = props.value || {}
        const value = modelValue[colorKey] || props.availableColors[colorKey] || ''
        return {
            key: colorKey,
            value
        }
    })
})

const getWidgetColors = computed(() => {
    const colors = props.onlyBackground ? BACKGROUND_COLORS : props.availableColors
    const colorEntries = Object.entries(colors)

    return colorEntries.filter(([ colorKey ]) => !mainColorKeys.value.includes(colorKey)).map(([ colorKey, colorValue ]) => {
        const key = colorKey as keyof Colors
        const modelValue: Colors = props.value || {}
        const value = modelValue[key] || colorValue
        return {
            key,
            value,
        }
    })
})
const predefinedColors = computed(() => {
    const options = values(activeLayoutColors.value)

    return uniq(options.filter(color => color.match(/^#(?:[0-9a-fA-F]{3}){1,2}$/)))
})

function onChangeColor (color: string, key: keyof Colors) {
    // console.log('onChangeColor', color, key)
    const newModel = model.value ? { ...model.value } : {} as Colors
    newModel[key] = color
    model.value = newModel
}

onMounted(() => {
    // console.log('activeLayout', activeLayout.value)
    // console.log('activeLayoutColors', activeLayoutColors.value)
    // console.log('getActiveLayoutColors', getActiveLayoutColors())
})
</script>
